import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Navbar from './components/Navbar'
import TransferRequestModal from './components/TransferRequestModal'
import GuidedTour from './components/GuidedTour'
import FeedbackWidget from './components/FeedbackWidget'
import ToastContainer from './components/ToastContainer'
import ErrorBoundary from './components/ErrorBoundary'
import Footer from './components/Footer'
import PageTransition from './components/PageTransition'
import { AppProvider } from './context/AppContext'
import { ToastProvider } from './context/ToastContext'
import { isCampaignPath } from './constants/campaigns'

const LandingPage = lazy(() => import('./pages/Landing'))
const PricingPage = lazy(() => import('./pages/Pricing'))
const SignupPage = lazy(() => import('./pages/Signup'))
const AccountPage = lazy(() => import('./pages/Account'))
const LogPage = lazy(() => import('./pages/Log'))
const PropertyPage = lazy(() => import('./pages/Property'))
const BuyerPage = lazy(() => import('./pages/Buyer'))
const SellerPage = lazy(() => import('./pages/Seller'))
const PublicReportPage = lazy(() => import('./pages/PublicReport'))
const RealtorShowingsPage = lazy(() => import('./pages/RealtorShowings'))
const RealtorDisclosurePage = lazy(() => import('./pages/RealtorDisclosure'))
const AdminPage = lazy(() => import('./pages/Admin'))
const InspectPage = lazy(() => import('./pages/Inspect'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'))
const ShopPage = lazy(() => import('./pages/Shop'))
const SellerFillPage = lazy(() => import('./pages/SellerFill'))
const PrivacyPage = lazy(() => import('./pages/Privacy'))
const TermsPage = lazy(() => import('./pages/Terms'))
const ContractorDashboardPage = lazy(() => import('./pages/ContractorDashboard'))
const FindContractorsPage = lazy(() => import('./pages/FindContractors'))
const ContractorPublicProfilePage = lazy(() => import('./pages/ContractorPublicProfile'))
const ForRealtorsPage = lazy(() => import('./pages/ForRealtors'))
const ForContractorsPage = lazy(() => import('./pages/ForContractors'))

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
    </div>
  )
}

function AppShell() {
  const hideChrome = isCampaignPath(useLocation().pathname)
  return (
    <div className="min-h-screen bg-navy">
      <ToastContainer />
      <TransferRequestModal />
      <GuidedTour />
      {!hideChrome && <FeedbackWidget />}
      {!hideChrome && <Navbar />}
      <ErrorBoundary>
        <PageTransition>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/log" element={<LogPage />} />
              <Route path="/property" element={<PropertyPage />} />
              <Route path="/buyer" element={<BuyerPage />} />
              <Route path="/seller" element={<SellerPage />} />
              <Route path="/report/:userId/:propertyId" element={<PublicReportPage />} />
              <Route path="/showings" element={<RealtorShowingsPage />} />
              <Route path="/disclosure/:userId/:propertyId" element={<RealtorDisclosurePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/inspect" element={<InspectPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/fill/:token" element={<SellerFillPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/contractor" element={<ContractorDashboardPage />} />
              <Route path="/find-contractors" element={<FindContractorsPage />} />
              <Route path="/contractors/:userId" element={<ContractorPublicProfilePage />} />
              <Route path="/for-realtors" element={<ForRealtorsPage />} />
              <Route path="/for-contractors" element={<ForContractorsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </PageTransition>
      </ErrorBoundary>
      {!hideChrome && <Footer />}
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AppProvider>
          <AppShell />
        </AppProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
